import { Controller } from "@hotwired/stimulus"
import { renderAiMarkdown } from "../lib/ai_markdown"

// Floating AI chat widget — Maple-style bottom-right bubble
// Connects to the existing AiService via a JSON endpoint
export default class extends Controller {
  static targets = ["panel", "toggle", "messages", "input", "sendBtn", "badge"]
  static values = {
    url: String,
    feedbackUrl: { type: String, default: "/ai_feedback" },
    conversationId: { type: Number, default: 0 }
  }

  connect() {
    this.open = false
    this.sending = false
    this.restoreSession()
  }

  // ── Toggle panel ────────────────────────────────────────────
  togglePanel() {
    this.open = !this.open
    this.panelTarget.classList.toggle("hidden", !this.open)
    this.panelTarget.classList.toggle("opacity-0", !this.open)
    this.panelTarget.classList.toggle("scale-95", !this.open)
    this.panelTarget.classList.toggle("opacity-100", this.open)
    this.panelTarget.classList.toggle("scale-100", this.open)

    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.add("hidden")
    }

    if (this.open) {
      setTimeout(() => this.inputTarget.focus(), 150)
      this.scrollToBottom()
    }
  }

  close() {
    this.open = false
    this.panelTarget.classList.add("hidden", "opacity-0", "scale-95")
    this.panelTarget.classList.remove("opacity-100", "scale-100")
  }

  // ── Send message ────────────────────────────────────────────
  async send(event) {
    event.preventDefault()
    const text = this.inputTarget.value.trim()
    if (!text || this.sending) return

    this.sending = true
    this.inputTarget.value = ""
    this.sendBtnTarget.disabled = true

    // Add user bubble
    this.appendMessage("user", text)
    this.scrollToBottom()

    // Show typing indicator
    const typingEl = this.showTyping()

    const abortController = new AbortController()
    const timeoutId = setTimeout(() => abortController.abort(), 30000)

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const body = { message: text }
      if (this.conversationIdValue) body.conversation_id = this.conversationIdValue

      const res = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(body),
        signal: abortController.signal
      })

      clearTimeout(timeoutId)
      const data = await res.json()
      typingEl.remove()

      if (data.success) {
        this.conversationIdValue = data.conversation_id
        this.saveSession()
        this.lastUserMessage = text
        this.lastCitedSources = data.cited_sources || []
        this.revealWords(data.content)
      } else {
        this.appendMessage("assistant", data.error || "Something went wrong. Please try again.")
      }
    } catch (err) {
      clearTimeout(timeoutId)
      typingEl.remove()
      if (err.name === "AbortError") {
        this.appendMessage("assistant", "The request timed out. Please try again.")
      } else {
        this.appendMessage("assistant", "Connection error. Please try again.")
      }
    } finally {
      this.sending = false
      this.sendBtnTarget.disabled = false
      this.inputTarget.focus()
    }
  }

  // ── Handle Enter key ────────────────────────────────────────
  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      this.send(event)
    }
  }

  // ── DOM helpers ─────────────────────────────────────────────
  appendMessage(role, text) {
    const wrapper = document.createElement("div")
    wrapper.className = role === "user"
      ? "flex justify-end mb-3"
      : "flex justify-start mb-3"

    const bubble = document.createElement("div")
    bubble.className = role === "user"
      ? "max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm bg-mod-charcoal text-white"
      : "max-w-[80%] rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm bg-mod-warm-100 text-mod-charcoal"

    if (role === "user") {
      bubble.textContent = text
    } else {
      bubble.innerHTML = this.renderMarkdown(text)
    }

    wrapper.appendChild(bubble)
    this.messagesTarget.appendChild(wrapper)
    this.scrollToBottom()
  }

  showTyping() {
    const wrapper = document.createElement("div")
    wrapper.className = "flex justify-start mb-3 typing-indicator"

    const bubble = document.createElement("div")
    bubble.className = "rounded-2xl rounded-bl-sm px-4 py-3 bg-mod-warm-100 flex gap-1.5 items-center"
    bubble.innerHTML = `
      <span class="w-2 h-2 bg-mod-warm-500 rounded-full animate-bounce" style="animation-delay:0ms"></span>
      <span class="w-2 h-2 bg-mod-warm-500 rounded-full animate-bounce" style="animation-delay:150ms"></span>
      <span class="w-2 h-2 bg-mod-warm-500 rounded-full animate-bounce" style="animation-delay:300ms"></span>
    `
    wrapper.appendChild(bubble)
    this.messagesTarget.appendChild(wrapper)
    this.scrollToBottom()
    return wrapper
  }

  // Word-by-word reveal animation (like Maple)
  // Animates with plain text, then snaps to rendered markdown on completion.
  revealWords(text) {
    const wrapper = document.createElement("div")
    wrapper.className = "flex justify-start mb-3"

    const col = document.createElement("div")
    col.className = "max-w-[80%]"

    const bubble = document.createElement("div")
    bubble.className = "rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm bg-mod-warm-100 text-mod-charcoal"
    bubble.textContent = ""

    col.appendChild(bubble)

    // Feedback buttons (thumbs up / thumbs down)
    const feedbackRow = document.createElement("div")
    feedbackRow.className = "flex items-center gap-1 mt-1 opacity-0 transition-opacity duration-300"

    const responseText = text
    const userMsg = this.lastUserMessage || ""
    const citedSources = this.lastCitedSources || []
    const self = this

    const thumbUp = document.createElement("button")
    thumbUp.className = "p-1 rounded hover:bg-green-50 text-mod-warm-400 hover:text-green-600 transition-colors"
    thumbUp.title = "Good response"
    thumbUp.innerHTML = `<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z M3 15h2"/></svg>`
    thumbUp.addEventListener("click", () => self.submitFeedback(2, userMsg, responseText, citedSources, feedbackRow))

    const thumbDown = document.createElement("button")
    thumbDown.className = "p-1 rounded hover:bg-red-50 text-mod-warm-400 hover:text-red-500 transition-colors"
    thumbDown.title = "Bad response"
    thumbDown.innerHTML = `<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z M21 4h-2"/></svg>`
    thumbDown.addEventListener("click", () => self.submitFeedback(1, userMsg, responseText, citedSources, feedbackRow))

    feedbackRow.appendChild(thumbUp)
    feedbackRow.appendChild(thumbDown)
    col.appendChild(feedbackRow)

    wrapper.appendChild(col)
    this.messagesTarget.appendChild(wrapper)

    const words = text.split(" ")
    let i = 0
    const interval = setInterval(() => {
      if (i < words.length) {
        bubble.textContent += (i > 0 ? " " : "") + words[i]
        i++
        this.scrollToBottom()
      } else {
        clearInterval(interval)
        // Replace accumulated plain text with fully-rendered markdown
        bubble.innerHTML = this.renderMarkdown(text)
        feedbackRow.classList.remove("opacity-0")
        feedbackRow.classList.add("opacity-100")
      }
    }, 35)
  }

  // ── Submit feedback (thumbs up/down) ─────────────────────────
  async submitFeedback(rating, userMessage, aiResponse, citedSources, feedbackRow) {
    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      await fetch(this.feedbackUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          rating: rating,
          user_message: userMessage,
          ai_response: aiResponse,
          conversation_id: this.conversationIdValue || null,
          cited_sources: citedSources,
          page: window.location.pathname
        })
      })

      // Replace buttons with confirmation
      const icon = rating === 2 ? "&#10003;" : "&#10005;"
      const color = rating === 2 ? "text-green-600" : "text-red-500"
      feedbackRow.innerHTML = `<span class="text-[11px] ${color}">Thanks for the feedback ${icon}</span>`
    } catch (err) {
      // Silent fail — feedback is non-critical
    }
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  // ── Session persistence (sessionStorage — cleared on tab close) ──
  saveSession() {
    if (this.conversationIdValue) {
      try {
        sessionStorage.setItem("mod_ai_conv", String(this.conversationIdValue))
      } catch (_) { /* ignore — private browsing or storage quota */ }
    }
  }

  restoreSession() {
    try {
      const saved = sessionStorage.getItem("mod_ai_conv")
      if (saved) this.conversationIdValue = parseInt(saved, 10)
    } catch (_) { /* ignore */ }
  }

  newConversation() {
    this.conversationIdValue = 0
    try { sessionStorage.removeItem("mod_ai_conv") } catch (_) { /* ignore */ }
    // Clear messages except greeting
    this.messagesTarget.innerHTML = ""
    this.appendMessage("assistant", "Hey! 👋 How can I help you today?")
    this.inputTarget.focus()
  }

  // ── Markdown renderer ────────────────────────────────────────
  // Handles the subset of Markdown the AI system prompt enables:
  // # Headings, **bold**, *italic*, `inline code`, ``` code blocks ```,
  // bullet lists, numbered lists.
  // HTML is escaped first so AI output cannot inject arbitrary markup.
  renderMarkdown(text) {
    return renderAiMarkdown(text)
  }
}
